import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Download, FileText, Inbox, Calendar, X } from 'lucide-react';
import { useForm, useFormSubmissions, useInviteToInterview } from '../hooks/useApi';
import { formatDateTime } from '../lib/utils';
import { cn } from '../lib/utils';

export default function FormSubmissionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: formData, isLoading: formLoading } = useForm(id || '');
  const { data: subData, isLoading: subLoading } = useFormSubmissions(id || '');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [inviteModalId, setInviteModalId] = useState<string | null>(null);
  const [inviteDate, setInviteDate] = useState('');
  const [inviteTime, setInviteTime] = useState('');
  const [inviteDuration, setInviteDuration] = useState(30);
  const [inviteNotes, setInviteNotes] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const inviteToInterview = useInviteToInterview();

  const openInviteModal = (applicantId: string) => {
    setInviteModalId(applicantId);
    setInviteDate('');
    setInviteTime('');
    setInviteDuration(30);
    setInviteNotes('');
  };

  const handleSendInvite = async () => {
    if (!inviteModalId || !inviteDate || !inviteTime) return;
    setInviteSending(true);
    try {
      const dateTime = new Date(`${inviteDate}T${inviteTime}`).toISOString();
      await inviteToInterview.mutateAsync({
        applicantId: inviteModalId,
        dateTime,
        duration: inviteDuration,
        notes: inviteNotes || undefined,
      });
      setInviteModalId(null);
    } catch (err) {
      console.error('Failed to send invite:', err);
    } finally {
      setInviteSending(false);
    }
  };

  const isLoading = formLoading || subLoading;
  const form = formData?.form;
  const submissions = subData?.submissions || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'reviewed':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/forms')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Submissions: {form?.name || 'Form'}
            </h2>
            <p className="text-sm text-gray-500">
              {form?.job_title} &middot; {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/forms/${id}`)}
          className="btn-secondary text-sm"
        >
          Edit Form
        </button>
      </div>

      {submissions.length === 0 ? (
        <div className="card p-12 text-center">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions yet</h3>
          <p className="text-gray-500">
            Submissions will appear here when applicants fill out the form.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-1" />
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1">Status</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100">
            {submissions.map((sub) => (
              <div key={sub.id}>
                {/* Summary row */}
                <button
                  onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                  className="w-full grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 text-left hover:bg-gray-50 transition-colors items-center"
                >
                  <div className="hidden md:flex col-span-1 items-center">
                    {expandedId === sub.id ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="col-span-3">
                    <span className="font-medium text-gray-900">
                      {sub.applicant_name || 'N/A'}
                    </span>
                    <span className="md:hidden text-sm text-gray-500 ml-2">
                      {sub.applicant_email}
                    </span>
                  </div>
                  <div className="hidden md:block col-span-3 text-sm text-gray-600 truncate">
                    {sub.applicant_email || 'N/A'}
                  </div>
                  <div className="hidden md:block col-span-2 text-sm text-gray-600">
                    {sub.applicant_phone || 'N/A'}
                  </div>
                  <div className="hidden md:block col-span-2 text-sm text-gray-500">
                    {formatDateTime(sub.submitted_at)}
                  </div>
                  <div className="col-span-1">
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize',
                        getStatusColor(sub.status)
                      )}
                    >
                      {sub.status}
                    </span>
                  </div>
                </button>

                {/* Expanded details */}
                {expandedId === sub.id && (
                  <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                    <div className="pt-4 space-y-4">
                      {/* Mobile info */}
                      <div className="md:hidden space-y-2 text-sm">
                        <p><span className="text-gray-500">Phone:</span> {sub.applicant_phone || 'N/A'}</p>
                        <p><span className="text-gray-500">Date:</span> {formatDateTime(sub.submitted_at)}</p>
                      </div>

                      {/* All field values */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Submission Data</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {Object.entries(sub.data).map(([key, value]) => (
                            <div key={key} className="bg-white rounded-lg p-3 border border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">{key}</p>
                              <p className="text-sm text-gray-900">
                                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                                  Array.isArray(value) ? value.join(', ') :
                                  String(value) || 'N/A'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Files */}
                      {sub.files && sub.files.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Uploaded Files</h4>
                          <div className="space-y-2">
                            {sub.files.map((file, i) => (
                              <a
                                key={i}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200 hover:border-primary-300 transition-colors"
                              >
                                <FileText className="w-5 h-5 text-gray-400" />
                                <span className="text-sm text-gray-700 flex-1 truncate">
                                  {file.filename}
                                </span>
                                <Download className="w-4 h-4 text-gray-400" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Invite to Interview */}
                      {sub.applicant_id && (
                        <div className="pt-2">
                          <button
                            onClick={() => openInviteModal(sub.applicant_id)}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                          >
                            <Calendar className="w-4 h-4" />
                            Invite to Interview
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Invite to Interview Modal */}
      {inviteModalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setInviteModalId(null)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Invite to Interview</h3>
              <button
                onClick={() => setInviteModalId(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={inviteDate}
                onChange={(e) => setInviteDate(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                value={inviteTime}
                onChange={(e) => setInviteTime(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <select
                value={inviteDuration}
                onChange={(e) => setInviteDuration(Number(e.target.value))}
                className="input"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={inviteNotes}
                onChange={(e) => setInviteNotes(e.target.value)}
                className="input resize-none"
                rows={3}
                placeholder="Any additional notes for the candidate..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setInviteModalId(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={!inviteDate || !inviteTime || inviteSending}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                {inviteSending ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
